import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./NavRoutes.scss";
import { logout } from "../../services/logout.service";
import { TiHome } from "react-icons/ti";
import {
  RiGovernmentLine,
  RiDashboardFill,
  RiAdminLine,
  RiNodeTree,
  RiQrCodeLine,
} from "react-icons/ri";
import { PiSignOut } from "react-icons/pi";
import { VscBook } from "react-icons/vsc";
import { FaPlug, FaRocket, FaDatabase } from "react-icons/fa";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";
import { authTokenStore } from "../../services/auth/tokenStore";

interface NavRoutesProps {
  isLoggedIn: boolean;
  routeBlocker: boolean;
  isCollapsed: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
}

interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: React.ReactNode;
  matchPaths?: string[];
  requiresFullAccess?: boolean;
  section?: "primary" | "secondary";
}

function NavRoutes({
  isLoggedIn,
  routeBlocker,
  isCollapsed,
  onToggleCollapse,
}: NavRoutesProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated =
    isLoggedIn || localStorage.getItem("isLoggedIn") === "true";

  const navItems: NavItem[] = useMemo(
    () => [
      {
        key: "home",
        label: "Home",
        to: "/home",
        matchPaths: ["/home"],
        icon: <TiHome className="tw-text-2xl" />,
      },
      {
        key: "dashboard",
        label: "Dashboard",
        to: "/dashboard",
        matchPaths: ["/dashboard"],
        icon: <RiDashboardFill className="tw-text-2xl" />,
      },
      {
        key: "dataSources",
        label: "Data Source Management",
        to: "/dataSources",
        matchPaths: ["/dataSources"],
        icon: <FaDatabase className="tw-text-xl" />,
      },
      {
        key: "dataModelAutomation",
        label: "Data Model Automation",
        to: "/ensembleModelling",
        matchPaths: ["/ensembleModelling", "/data-vault-modelling", "/data-mart-modelling", "/importDataModel"],
        requiresFullAccess: true,
        icon: <RiQrCodeLine className="tw-text-xl" />,
      },
      {
        key: "orchestration",
        label: "Orchestration",
        to: "/jobDependency",
        matchPaths: ["/jobDependency", "/jobManagement"],
        icon: <RiNodeTree className="tw-text-xl" />,
      },
      {
        key: "governance",
        label: "Governance",
        to: "/data-lineage-v2",
        matchPaths: ["/data-lineage-v2"],
        icon: <RiGovernmentLine className="tw-text-2xl" />,
      },
      {
        key: "metaDeployment",
        label: "Meta Data Deployment",
        to: "/metaDataDeployment",
        matchPaths: ["/metaDataDeployment", "/deploymentHistory"],
        requiresFullAccess: true,
        icon: <FaRocket className="tw-text-xl" />,
      },
      {
        key: "dataCatalog",
        label: "Data Catalog",
        to: "/dataCatalog",
        matchPaths: ["/dataCatalog"],
        icon: <VscBook className="tw-text-2xl" />,
        section: "secondary",
      },
      {
        key: "customPlugin",
        label: "Plug-in Management",
        to: "/customPlugin",
        matchPaths: ["/customPlugin"],
        icon: <FaPlug className="tw-text-xl" />,
        section: "secondary",
      },
      {
        key: "admin",
        label: "Admin",
        to: "/adminPage",
        matchPaths: ["/adminPage"],
        icon: <RiAdminLine className="tw-text-2xl" />,
        section: "secondary",
      },
    ],
    []
  );

  const filteredItems = navItems.filter(
    (item) => !(item.requiresFullAccess && routeBlocker)
  );

  const primaryItems = filteredItems.filter(
    (item) => item.section !== "secondary"
  );
  const secondaryItems = filteredItems.filter(
    (item) => item.section === "secondary"
  );

  const isItemActive = (item: NavItem) => {
    if (item.matchPaths && item.matchPaths.length > 0) {
      return item.matchPaths.some((path) =>
        location.pathname.startsWith(path)
      );
    }

    return location.pathname === item.to;
  };

  const handleToggle = () => {
    onToggleCollapse?.(!isCollapsed);
  };

  const onSignOutHandler = async () => {
    try {
      await logout();
    } catch {
      // ignore logout errors
    }
    authTokenStore.clearTokens();
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("environment_id");
    localStorage.removeItem("user_id");
    localStorage.removeItem("customer_id");
    localStorage.removeItem("email");
    navigate("/", { replace: true });
  };

  const renderNavList = (items: NavItem[], compact?: boolean) =>
    items.map((item) => {
      const active = isItemActive(item);
      return (
        <Link
          key={item.key}
          to={item.to}
          className={`sidebar-nav-link ${active ? "active" : ""} ${isCollapsed ? "collapsed" : ""
            } ${compact ? "compact" : ""}`}
          title={isCollapsed ? item.label : undefined}
          aria-label={item.label}
        >
          <span className={`sidebar-nav-link-icon ${active ? "active" : ""}`}>
            {item.icon}
          </span>
          {!isCollapsed && (
            <span className="sidebar-nav-link-label">{item.label}</span>
          )}
        </Link>
      );
    });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div
      className={`side-navbar-routes-container ${isCollapsed ? "collapsed" : ""
        }`}
      data-master-exception
    >
      <div className="nav-header">
        {!isCollapsed && <span className="nav-header-title">Navigation</span>}
        <button
          type="button"
          onClick={handleToggle}
          className="nav-toggle"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <HiOutlineChevronRight /> : <HiOutlineChevronLeft />}
        </button>
      </div>

      <div className="nav-section">{renderNavList(primaryItems)}</div>

      {secondaryItems.length > 0 && (
        <div className="nav-section secondary">
          {!isCollapsed && <span className="nav-section-title">Tools</span>}
          {renderNavList(secondaryItems, true)}
        </div>
      )}

      <button
        type="button"
        onClick={onSignOutHandler}
        className={`sign-out ${isCollapsed ? "collapsed" : ""}`}
      >
        <span className="sign-out-icon">
          <PiSignOut />
        </span>
        {!isCollapsed && <span>Sign Out</span>}
      </button>
    </div>
  );
}

export default NavRoutes;
